/* eslint-disable react/prop-types */
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useRequestData } from 'wsm-common-data';
import PropTypes from 'prop-types';
import {
	isProgramAspectRatioAccount,
	getProgramAspectRatio,
	getProgramAspectRatioAccountSync,
	isMazdaVariation
} from '../../utilities/aspect';
import {
	aR as ASPECT_RATIO,
	aspectRatiosProportions
} from '../../global-constants';
import './AspectRatioBox.scss';
import { PrefsContext } from '../../contexts';

const AspectRatioBox = ({ children, innerWrapperStyle, isSkeleton }) => {
	const { aspectRatio } = useContext(PrefsContext);
	const { deviceType } = useRequestData();
	const [widthProportion, heightProportion] =
		aspectRatiosProportions.get(aspectRatio);
	const containerStyle = {};
	// Only applies to slideshow
	let ratioStyle =
		aspectRatio !== ASPECT_RATIO.MOBILE_4X3_COUPON &&
		aspectRatio !== ASPECT_RATIO.DESKTOP_4X3
			? `${widthProportion}/${heightProportion}`
			: undefined;
	const account = useSelector((state) => state.account);
	const { id: accountId } = useSelector((state) => state.accountId);
	const variationRef = useSelector((state) => state.variationRef);
	const isSlideshow =
		aspectRatio === ASPECT_RATIO.DESKTOP_4X1 ||
		aspectRatio === ASPECT_RATIO.MOBILE_4X3_SLIDESHOW;
	// `account` (with topLevelAccount) is only populated after /promos resolves,
	// and some demo/test accounts (e.g. 'poseidondemo') don't reveal the OEM via
	// accountId/topLevelAccount at all, so also fall back to variationRef (fetched
	// server-side from site-config-service, confirmed to identify Mazda sites) to
	// detect the program. This keeps the skeleton box the same height as the loaded
	// content and avoids a layout shift (CLS).
	const programAspectRatioAccount =
		isProgramAspectRatioAccount(account) ||
		getProgramAspectRatioAccountSync(accountId) ||
		(isMazdaVariation(variationRef) ? 'mazda' : undefined);
	// `deviceType` guard: the accountId-based fallback above can resolve
	// `programAspectRatioAccount` earlier in the render lifecycle than before
	// (it no longer waits on `account` to load), so `deviceType` isn't
	// guaranteed to be populated yet in every context this now runs in.
	const programAspectRatio =
		programAspectRatioAccount && isSlideshow && deviceType
			? getProgramAspectRatio(programAspectRatioAccount, variationRef)[
					deviceType.toLowerCase()
			  ]
			: undefined;
	const isFixedProgramAspectRatio =
		programAspectRatioAccount === 'mazda' ||
		programAspectRatioAccount === 'stellantis';
	const fixedProgramAspectRatio =
		programAspectRatio && isFixedProgramAspectRatio;
	if (fixedProgramAspectRatio) {
		ratioStyle = `${programAspectRatio.width}/${programAspectRatio.height}`;
		containerStyle.aspectRatio = ratioStyle;
		containerStyle.height = 'auto';
		// Override the padding-top reserved by the `.aspect-ratio-{w}-{h}` class
		// (e.g. 25% for 4:1). Without this, the box reserves height from BOTH the
		// class padding AND the program aspect-ratio, stacking to a taller box
		// than the real content and causing a layout shift (CLS) on load.
		containerStyle.paddingTop = `calc(${programAspectRatio.height} / ${programAspectRatio.width} * 100%)`;
	}
	return (
		<div
			data-testid="aspect-ratio-box"
			className={`aspect-ratio-${widthProportion}-${heightProportion}`}
			style={{ ...containerStyle, aspectRatio: ratioStyle }}
		>
			<div
				data-testid="inner-wrapper"
				className={`aspect-ratio-box-inner-wrapper ${
					fixedProgramAspectRatio && !isSkeleton
						? 'fixed-program-aspect-ratio'
						: ''
				} `}
				style={{ ...innerWrapperStyle, border: '0' }}
			>
				{children}
			</div>
		</div>
	);
};

AspectRatioBox.propTypes = {
	innerWrapperStyle: PropTypes.shape({}),
	isSkeleton: PropTypes.bool
};

export default AspectRatioBox;
